/* tslint:disable:max-line-length */
import SkeletonGuildPages from '../../../../../shared/frontend/components/guild/SkeletonGuildPages';
import SkeletonRaidRankingsPage from '../../../../../shared/frontend/components/layouts/SkeletonRaidRankingsPage';
import { TRouteDef } from '../../../../../shared/frontend/libs/router/router-helpers';
import { EventMode } from '../../../../../shared/types/eventTypes';
import App from '../components/presentational/app';
import NotFoundPage from '../../../../../shared/frontend/components/presentational/notFoundPage';
import { TSiteConfigState } from '../libs/store';
import { namedRoutes, resolveRoutePath } from './routes';
import RootNode from './rootNode';
import loadable from '@loadable/component';

// ==============================================
// STATIC ROUTE IMPORTS
//
// These are static imports so these routes are
// available in the main bundle for faster access
// ==============================================

import HomePage from '../components/container/homePage';

// ==============================================
// Pages we MUST run through SSR
// ==============================================
const MythicPlusTeamPageLayout = loadable(() => import('../team/containers/MythicPlusTeamPageLayout'));
const TeamRankingsPage = loadable(() => import('../team/containers/teamRankingsPage'));
const TeamRankingsEventPage = loadable(() => import('../team/containers/teamRankingsEventPage'));
const GuildPageContainer = loadable(() => import('../guild/containers/guildPageContainer'));

// ==============================================
// Pages used in multiple routes
// ==============================================
const RaidRankingsPage = loadable(() => import('../components/container/raidRankingsPage'));
const ProgressRankingsPage = loadable(() => import('../components/container/progressRankingsPage'));
const KeystoneSpecRankingsPage = loadable(() => import('../components/container/keystoneSpecRankingsPage'));
const KeystoneRunRankingsPage = loadable(() => import('../components/container/keystoneRunRankingsPage'));
const KeystoneCharacterRankingsPage = loadable(() => import('../components/container/keystoneCharacterRankingsPage'));
const BossRankingsPage = loadable(() => import('../components/container/bossRankingsPage'));
const RaidHallOfFamePage = loadable(() => import('../components/container/raidHallOfFamePage'));
const SpecificHomePage = loadable(() => import('../components/container/specificHomePage'));
const EventsLayout = loadable(() => import('../../../../../shared/frontend/scripts/events/containers/EventsLayout'));

// ==============================================
// Non-SSR Pages
// ==============================================
const MythicPlusReplaysPage = loadable(() => import('../replaysManagement/containers/MythicPlusReplaysPage'));
const CharacterDetailsPage = loadable(() => import('../character/containers/detailsPage'));
const EventManagementLayout = loadable(() => import('../eventsManagement/containers/EventManagementLayout'));
const MessageCenterLayout = loadable(() => import('../messageCenter/components/messageCenterLayout'));
const TimelineEventManagementLayout = loadable(
  () => import('../timelineEventManagement/containers/TimelineEventManagementLayout')
);
const SiteModerationLayout = loadable(() => import('../siteModeration/containers/SiteModerationLayout'));
const CreateApplicationPage = loadable(() => import('../applicationsManagement/containers/CreateApplicationPage'));
const ApplicationManagementPage = loadable(
  () => import('../applicationsManagement/containers/ApplicationManagementPage')
);
const ApplicationsManagementPage = loadable(
  () => import('../applicationsManagement/containers/ApplicationsManagementPage')
);
const RecruitmentProfileCreation = loadable(
  () => import('../recruitmentProfiles/containers/recruitmentProfileCreation')
);
const RecruitmentProfilesPage = loadable(() => import('../recruitmentSettings/containers/recruitmentProfilesPage'));
const RecruitmentProfileEditing = loadable(() => import('../recruitmentProfiles/containers/recruitmentProfileEditing'));

export function getRoutes(
  siteConfig: Pick<
    TSiteConfigState,
    'currentRaidSlug' | 'currentRaidDifficulty' | 'nextRaidSlug' | 'currentSeasonSlug' | 'currentSeasonCutoffSlug'
  >
) {
  const routes: TRouteDef[] = [
    // Redirect Routes
    {
      path: '/',
      children: [
        {
          from: '/home',
          to: '/'
        },
        {
          from: '/recruit',
          to: '/recruitment'
        },
        {
          from: '/replays',
          to: namedRoutes.ReplaysSettings
        },
        {
          from: '/midnight-affixes',
          to: '/news/740-midnight-mythic-plus-affixes'
        },
        {
          from: '/the-war-within-affixes',
          to: '/news/667-the-war-within-mythic-plus-affixes'
        },
        {
          from: '/tww-affixes',
          to: '/the-war-within-affixes'
        },
        {
          from: '/dragonflight-affixes',
          to: '/news/505-dragonflight-season-2-mythic-plus-affixes'
        },
        {
          from: '/shadowlands-affixes',
          to: '/news/118-shadowlands-affixes'
        },
        {
          from: '/mythic-plus-101-what-is-mythic-plus',
          to: '/news/14-mythic-plus-101-why-should-you-run-mythic-plus'
        },
        {
          from: '/mythic-plus-101-what-you-need-to-know-for-your-first-mythic-run',
          to: '/news/51-mythic-plus-101-what-you-need-to-know-for-your-first-mythic-run'
        },
        {
          from: '/mythic-plus-101-pugging-networking-and-group-dynamics',
          to: '/news/228-mythic-plus-101-pugging-networking-and-group-dynamic'
        },
        {
          from: '/affixes',
          to: '/midnight-affixes'
        },
        {
          from: '/:raid/world-first-race',
          to: namedRoutes.RaidWorldFirstTracker
        },
        {
          from: '/:raid/race-to-world-first',
          to: namedRoutes.RaidGlobalCoverage
        },
        {
          from: '/rwf',
          to: resolveRoutePath(namedRoutes.RaidGlobalCoverage, {
            raid: siteConfig.nextRaidSlug || siteConfig.currentRaidSlug
          })
        },
        {
          from: '/rtwf',
          to: '/rwf'
        },
        {
          from: '/race-to-world-first',
          to: '/rwf'
        },
        {
          from: '/rwf/analyze',
          to: resolveRoutePath(namedRoutes.RWFAnalyzerPage, {
            raid: siteConfig.nextRaidSlug || siteConfig.currentRaidSlug
          })
        },
        {
          from: '/rwf/analyzer',
          to: '/rwf/analyze'
        },
        {
          from: '/u/:name',
          to: namedRoutes.UserProfile
        },
        {
          from: '/:raid/realms/:region/heroic',
          to: namedRoutes.ConnectedRealms
        },
        {
          from: '/:raid/realms/:region/normal',
          to: namedRoutes.ConnectedRealms
        },
        {
          from: '/:raid/realms/:region/mythic',
          to: namedRoutes.ConnectedRealms
        },
        {
          from: '/realms/world',
          to: namedRoutes.ConnectedRealms
        },
        {
          from: '/mythic-dungeon-international/summer-2019-brackets',
          to: namedRoutes.MdiSummer2019BracketsFinals
        },
        {
          from: '/mythic-dungeon-international/summer-2019-time-trials',
          to: resolveRoutePath(namedRoutes.MythicDungeonInvitationalSummer2019TimeTrialsOverview, { trial: 'east-3' })
        },
        {
          // Legacy redirect - hardcoded bracket slug to avoid importing mdi.ts (~40KB)
          from: '/mythic-dungeon-international/bfa-season-4-brackets',
          to: resolveRoutePath(namedRoutes.MythicDungeonInternationalBracketBfa2020, {
            bracket: 'global-finals'
          })
        },
        {
          from: '/mdi',
          to: '/tournaments/mdi'
        },
        {
          from: '/mythic-dungeon-international',
          to: '/tournaments/mdi'
        },
        {
          from: '/awc',
          to: '/tournaments/awc'
        },
        {
          from: '/arena-world-championship',
          to: '/tournaments/awc'
        },
        {
          from: '/routes',
          to: '/weekly-routes'
        },
        {
          from: '/route',
          to: '/weekly-routes'
        },
        {
          from: '/mythic-dungeon-invitational-2018',
          to: namedRoutes.MythicDungeonInvitational2018GlobalLanding
        },
        {
          from: '/mythic-dungeon-invitational-allstars-2018',
          to: namedRoutes.MythicDungeonInvitational2018AllStarsLanding
        },
        {
          from: '/mythic-dungeon-invitational-allstars-2018/brackets/blizzcon-allstars',
          to: '/mythic-dungeon-invitational-allstars-2018/bracket'
        },
        { from: '/add', to: '/' },
        { from: '/add/success', to: '/' },
        { from: '/mythic-plus/realms', to: '/mythic-plus/realms/us/current' },
        { from: '/mythic-plus/realms/:region', to: '/mythic-plus/realms/:region/current' },

        {
          from: '/mythic-plus/cutoffs',
          to: `/mythic-plus/cutoffs/${siteConfig.currentSeasonCutoffSlug}/us`
        },

        {
          from: '/mythic-plus-rankings/season-sl-3-class-clash/all/world/leaderboards-strict',
          to: '/mythic-plus-rankings/season-sl-3-event-class-clash/all/world/leaderboards-strict'
        },

        {
          from: '/settings/groups/:group_id/live-tracking',
          to: namedRoutes.UserGroupPrivacySettings
        },

        //
        // Raiding
        //
        {
          from: '/raids',
          to: `/${siteConfig.currentRaidSlug}/rankings/world/${siteConfig.currentRaidDifficulty}`
        },
        {
          from: '/raids/:region/:realm',
          to: `/${siteConfig.currentRaidSlug}/rankings/:region/:realm/${siteConfig.currentRaidDifficulty}`
        },
        {
          from: '/raid',
          to: `/${siteConfig.currentRaidSlug}/rankings/world/${siteConfig.currentRaidDifficulty}`
        },
        {
          from: `/${siteConfig.currentRaidSlug}`,
          to: `/${siteConfig.currentRaidSlug}/rankings/world/${siteConfig.currentRaidDifficulty}`
        },

        // These are old raids that we do not have accurate heroic/normal progress for
        {
          from: '/the-emerald-nightmare/rankings/:region/heroic',
          to: '/the-emerald-nightmare/rankings/:region/mythic'
        },
        {
          from: '/the-emerald-nightmare/rankings/:region/normal',
          to: '/the-emerald-nightmare/rankings/:region/mythic'
        },
        {
          from: '/the-emerald-nightmare/rankings/:region/heroic/:page',
          to: '/the-emerald-nightmare/rankings/:region/mythic/:page'
        },
        {
          from: '/the-emerald-nightmare/rankings/:region/normal/:page',
          to: '/the-emerald-nightmare/rankings/:region/mythic/:page'
        },
        { from: '/the-emerald-nightmare', to: '/the-emerald-nightmare/rankings/world/mythic' },

        { from: '/trial-of-valor/rankings/:region/heroic', to: '/trial-of-valor/rankings/:region/mythic' },
        { from: '/trial-of-valor/rankings/:region/normal', to: '/trial-of-valor/rankings/:region/mythic' },
        {
          from: '/trial-of-valor/rankings/:region/heroic/:page',
          to: '/trial-of-valor/rankings/:region/mythic/:page'
        },
        {
          from: '/trial-of-valor/rankings/:region/normal/:page',
          to: '/trial-of-valor/rankings/:region/mythic/:page'
        },
        { from: '/trial-of-valor', to: '/trial-of-valor/rankings/world/mythic' },

        { from: '/uldir', to: '/uldir/rankings/world/mythic' },
        { from: '/antorus-the-burning-throne', to: '/antorus-the-burning-throne/rankings/world/mythic' },
        { from: '/tomb-of-sargeras', to: '/tomb-of-sargeras/rankings/world/mythic' },
        { from: '/the-nighthold', to: '/the-nighthold/rankings/world/mythic' },

        //
        // MythicPlus
        //
        {
          from: '/mythic-plus',
          to: `/mythic-plus/${siteConfig.currentSeasonSlug}/all/world/leaderboards-strict`
        },
        {
          from: '/mythic-plus/:region',
          to: `/mythic-plus/${siteConfig.currentSeasonSlug}/all/:region/leaderboards-strict`
        },
        {
          from: '/mythic',
          to: `/mythic-plus/${siteConfig.currentSeasonSlug}/all/world/leaderboards-strict`
        },
        {
          from: '/mythicplus',
          to: `/mythic-plus/${siteConfig.currentSeasonSlug}/all/world/leaderboards-strict`
        },
        {
          from: '/mythic-keystone',
          to: `/mythic-plus/${siteConfig.currentSeasonSlug}/all/world/leaderboards-strict`
        },
        {
          from: '/mythickeystone',
          to: `/mythic-plus/${siteConfig.currentSeasonSlug}/all/world/leaderboards-strict`
        },
        {
          from: '/eve-ascension-df-season-2/teams',
          to: '/team-rankings/season-df-2/world/10087'
        },
        {
          from: '/replays',
          to: resolveRoutePath(namedRoutes.KeystoneRunRankings, {
            season: siteConfig.currentSeasonSlug,
            dungeon: 'all',
            region: 'world',
            type: 'leaderboards'
          })
        },

        {
          from: '/guilds/:region/:realm/:guild/mythic-plus-characters',
          to: `/guilds/:region/:realm/:guild/mythic-plus-characters/${siteConfig.currentSeasonSlug}`
        },

        { from: '/mythic-dungeon-invitational/us', to: '/mythic-dungeon-invitational/americas' },
        { from: '/mythic-dungeon-invitational/eu', to: '/mythic-dungeon-invitational/europe' },
        { from: '/mythic-dungeon-invitational/oc', to: '/mythic-dungeon-invitational/apac' },
        { from: '/mythic-dungeon-invitational/kr', to: '/mythic-dungeon-invitational/apac' },
        { from: '/mythic-dungeon-invitational/tw', to: '/mythic-dungeon-invitational/apac' },

        { from: '/mythic-plus/runs/:id', to: '/mythic-plus/season-7.2.0/runs/:id' },

        {
          from: '/mythic-plus/:season/:dungeon/:region/:type/affixes/:affixes',
          to: '/mythic-plus-affix-rankings/:season/:dungeon/:region/:type/:affixes'
        },

        {
          from: '/mythic-plus/:season/:dungeon/:region/:type/affixes/:affixes/:page',
          to: '/mythic-plus-affix-rankings/:season/:dungeon/:region/:type/:affixes/:page'
        },

        {
          from: '/mythic-plus/:season/:dungeon/:region/:type',
          to: '/mythic-plus-rankings/:season/:dungeon/:region/:type'
        },

        {
          from: '/mythic-plus/:season/:dungeon/:region/:type/:page',
          to: '/mythic-plus-rankings/:season/:dungeon/:region/:type/:page'
        },

        {
          from: '/mythic-plus/:dungeon/:region/leaderboards-character/:class/:role',
          to: '/mythic-plus-character-rankings/:dungeon/:region/:class/:role'
        },

        {
          from: '/mythic-plus/:dungeon/:region/leaderboards-character/:class/:role/:page',
          to: '/mythic-plus-character-rankings/:dungeon/:region/:class/:role/:page'
        },

        {
          from: '/mythic-plus/:dungeon/:region/leaderboards-character/:class/:role',
          to: '/mythic-plus-character-realm-rankings/:dungeon/:region/:class/:role'
        },

        {
          from: '/mythic-plus/:dungeon/:region/:realm/leaderboards-character/:class/:role/:page',
          to: '/mythic-plus-character-realm-rankings/:dungeon/:region/:realm/:class/:role/:page'
        },

        {
          from: '/mythic-plus/:season/runs/:id',
          to: namedRoutes.MythicKeystoneRun
        },

        //
        // FOOP
        //
        {
          from: '/mythic-plus/:dungeon/:region/leaderboards/:page',
          to: '/mythic-plus/season-7.2.0/:dungeon/:region/leaderboards/:page'
        },
        {
          from: '/mythic-plus/:dungeon/:region/leaderboards',
          to: '/mythic-plus/season-7.2.0/:dungeon/:region/leaderboards'
        },

        {
          from: '/mythic-plus/:dungeon/:region/leaderboards-strict/:page',
          to: '/mythic-plus/season-7.2.0/:dungeon/:region/leaderboards-strict/:page'
        },
        {
          from: '/mythic-plus/:dungeon/:region/leaderboards-strict',
          to: '/mythic-plus/season-7.2.0/:dungeon/:region/leaderboards-strict'
        },
        {
          from: '/mythic-plus/:region/leaderboards-character/:class/:role/:page',
          to: '/mythic-plus/season-7.2.0/:region/leaderboards-character/:class/:role/:page'
        },
        {
          from: '/mythic-plus/:region/leaderboards-character/:class/:role',
          to: '/mythic-plus/season-7.2.0/:region/leaderboards-character/:class/:role'
        },

        { from: '/mythic-invitational-2017', to: '/mythic-plus/season-7.2.5/world/leaderboards-team' },

        /* 11/11/2019 - Blizzard suddenly no longer supports slug names without accents... */
        ...[
          ['aggra-portugues', 'aggra-português'],
          ['chants-eternels', 'chants-éternels'],
          ['confrerie-du-thorium', 'confrérie-du-thorium'],
          ['festung-der-sturme', 'festung-der-stürme'],
          ['la-croisade-ecarlate', 'la-croisade-écarlate'],
          ['marecage-de-zangar', 'marécage-de-zangar'],
          ['pozzo-delleternita', 'pozzo-delleternità']
        ].flatMap(([oldSlug, newSlug]: string[]) => [
          {
            from: `/characters/eu/${oldSlug}/:character`,
            to: `/characters/eu/${newSlug}/:character`
          },
          { from: `/guilds/eu/${oldSlug}/:guild`, to: `/guilds/eu/${newSlug}/:guild` },
          {
            from: `/guilds/eu/${oldSlug}/:guild/:section?`,
            to: `/guilds/eu/${newSlug}/:guild/:section?`
          },
          {
            from: `/guilds/eu/${oldSlug}/:guild/:section?/:section2?`,
            to: `/guilds/eu/${newSlug}/:guild/:section/:section2?`
          },
          {
            from: `/:raid/boss-faction-realm-rankings/:boss/eu/${oldSlug}/:difficulty/:faction/:page?`,
            to: `/:raid/boss-faction-realm-rankings/:boss/eu/${newSlug}/:difficulty/:faction/:page?`
          },
          {
            from: `/:raid/boss-realm-rankings/:boss/eu/${oldSlug}/:difficulty/:page?`,
            to: `/:raid/boss-realm-rankings/:boss/eu/${newSlug}/:difficulty/:page?`
          },
          {
            from: `/mythic-plus-character-realm-rankings/:season/eu/${oldSlug}/:class/:role/:page?`,
            to: `/mythic-plus-character-realm-rankings/:season/eu/${newSlug}/:class/:role/:page?`
          },
          {
            from: `/mythic-plus-spec-realm-rankings/:season/eu/${oldSlug}/:class/:spec/:page?`,
            to: `/mythic-plus-spec-realm-rankings/:season/eu/${newSlug}/:class/:spec/:page?`
          },
          {
            from: `/:raid/progress-realm-rankings/:progress/eu/${oldSlug}/:difficulty/:page?`,
            to: `/:raid/progress-realm-rankings/:progress/eu/${newSlug}/:difficulty/:page?`
          },
          {
            from: `/:raid/realm-rankings/eu/${oldSlug}/:difficulty/:page?`,
            to: `/:raid/realm-rankings/eu/${newSlug}/:difficulty/:page?`
          },
          {
            from: `/:raid/faction-realm-rankings/eu/${oldSlug}/:difficulty/:faction/:page?`,
            to: `/:raid/faction-realm-rankings/eu/${newSlug}/:difficulty/:faction/:page?`
          }
        ]),
        {
          from: '/event-insights',
          to: namedRoutes.EventInsights
        },
        {
          from: '/tournaments/community',
          to: resolveRoutePath(namedRoutes.CommunityEvents, {})
        },
        {
          from: '/tournaments/community/completed',
          to: resolveRoutePath(namedRoutes.CommunityEvents, { mode: EventMode.COMPLETED })
        },
        {
          from: '/tournaments/active',
          to: resolveRoutePath(namedRoutes.TournamentsHub, {})
        },
        {
          from: '/tournaments/all',
          to: resolveRoutePath(namedRoutes.TournamentsHub, {})
        },
        {
          from: '/tournaments/completed',
          to: resolveRoutePath(namedRoutes.TournamentsHub, {})
        },
        {
          from: '/transmog',
          to: '/transmogs'
        },
        {
          from: '/event-manager',
          to: namedRoutes.EventsManagement
        },
        {
          from: '/statistics/:type?',
          to: '/stats/:type?'
        }
      ]
    },

    {
      path: '/',
      component: App,
      children: [
        // Settings Routes
        {
          path: '/settings',
          component: loadable(() => import('../components/presentational/appSettings')),
          children: [
            {
              path: namedRoutes.UserSettings,
              component: loadable(() => import('../components/container/settings/userSettingsNavOrRedirect'))
            },
            {
              path: namedRoutes.UserTimeSettings,
              component: loadable(() => import('../components/container/settings/userTimeSettingsPage'))
            },
            {
              path: namedRoutes.UserProfileSettings,
              component: loadable(() => import('../components/container/settings/userProfileSettingsPage'))
            },
            {
              path: namedRoutes.UserBlockList,
              component: loadable(() => import('../components/container/settings/userBlockListPage'))
            },
            {
              path: namedRoutes.UserBattleNetSettings,
              component: loadable(() => import('../components/container/settings/userBattleNetSettingsPage'))
            },
            {
              path: namedRoutes.UserEmailSettings,
              component: loadable(() => import('../components/container/settings/userEmailSettingsPage'))
            },

            {
              path: namedRoutes.UserCharacterProfileSettings,
              component: loadable(() => import('../components/container/settings/userCharacterProfileSettingsPage'))
            },

            {
              path: namedRoutes.UserCharacterPrivacySettings,
              component: loadable(() => import('../components/container/settings/userCharacterPrivacySettingsPage'))
            },

            {
              path: namedRoutes.UserCharacterTransferSettings,
              component: loadable(() => import('../components/container/settings/userCharacterTransferSettingsPage'))
            },

            {
              path: namedRoutes.UserCharacterAddonSettings,
              component: loadable(() => import('../components/container/settings/userCharacterAddonSettingsPage'))
            },

            {
              path: namedRoutes.UserPasswordSettings,
              component: loadable(() => import('../components/container/settings/userPasswordSettingsPage'))
            },

            {
              path: namedRoutes.UserTwoFactorAuth,
              component: loadable(() => import('../components/container/settings/userTwoFactorAuthSettingsPage'))
            },

            {
              path: namedRoutes.UserUsernameSettings,
              component: loadable(() => import('../components/container/settings/userUsernameSettingsPage'))
            },

            {
              path: namedRoutes.CreateUserApplication,
              component: CreateApplicationPage
            },

            {
              path: namedRoutes.UserApplication,
              component: ApplicationManagementPage
            },

            {
              path: namedRoutes.UserApplications,
              component: ApplicationsManagementPage
            },

            {
              path: namedRoutes.UserSocialSettings,
              component: loadable(() => import('../components/container/settings/userSocialSettingsPage'))
            },

            {
              path: namedRoutes.UserNotificationSettings,
              component: loadable(() => import('../components/container/settings/userNotificationSettingsPage'))
            },

            {
              from: namedRoutes.UserPatreonSettings,
              to: namedRoutes.UserSubscriptionsSettings
            },

            {
              path: namedRoutes.UserSubscriptionsSettings,
              component: loadable(() => import('../components/container/settings/userSubscriptionsSettingsPage'))
            },

            {
              path: namedRoutes.UserPremiumSettings,
              component: loadable(() => import('../components/container/settings/userPremiumSettingsPage'))
            },

            {
              path: namedRoutes.UserAddonSettings,
              component: loadable(() => import('../components/container/settings/userAddonSettingsPage'))
            },

            {
              path: namedRoutes.UserDeleteAccountSettings,
              component: loadable(() => import('../components/container/settings/userDeleteAccountPage'))
            },

            //
            // Group Settings
            //
            {
              path: namedRoutes.UserGroupSettings,
              component: loadable(() => import('../components/container/settings/userGroupSettingsPage'))
            },
            {
              path: namedRoutes.UserGroupRoleSettings,
              component: loadable(() => import('../components/container/settings/userGroupRoleSettingsPage'))
            },
            {
              path: namedRoutes.UserGroupPrivacySettings,
              component: loadable(
                () =>
                  import('../../../../../shared/frontend/components/settings/groupCharter/userGroupPrivacySettingsPage')
              )
            },
            {
              path: namedRoutes.UserGroupClientUsage,
              component: loadable(() => import('../components/container/settings/userGroupClientUsagePage'))
            },
            {
              path: namedRoutes.UserGroupBlockListSettings,
              component: loadable(() => import('../components/container/settings/userGroupBlockListPage'))
            },

            //
            // Charter Settings
            //
            {
              path: namedRoutes.CreateGroupCharterSettings,
              component: loadable(() => import('../components/container/settings/groupCharter/createGroupCharterPage'))
            },
            {
              path: namedRoutes.ViewGroupCharterSettings,
              component: loadable(() => import('../components/container/settings/groupCharter/viewGroupCharterPage'))
            },
            {
              path: namedRoutes.RenameGroupCharterPage,
              component: loadable(() => import('../components/container/settings/groupCharter/renameGroupCharterPage'))
            },

            // Recruitment Profiles
            // recruitment profile creation
            {
              path: namedRoutes.RecruitmentProfileCreate,
              component: RecruitmentProfileCreation
            },
            {
              path: namedRoutes.UserRecruitmentProfiles,
              component: RecruitmentProfilesPage
            },
            // recruitment profile editing
            {
              path: namedRoutes.RecruitmentProfileConfig,
              component: RecruitmentProfileEditing
            },

            //
            // Replays
            //
            {
              path: namedRoutes.ReplaysSettings,
              component: MythicPlusReplaysPage
            }
          ]
        },

        // Message Center Routes
        {
          path: namedRoutes.MessageCenterHomePage,
          component: MessageCenterLayout,
          children: [
            {
              path: namedRoutes.MessageCenterHomePage,
              component: loadable(() => import('../messageCenter/containers/messageCenterHome'))
            },
            {
              path: namedRoutes.MessageCenterThread,
              component: loadable(() => import('../messageCenter/containers/messageCenterThread'))
            }
          ]
        },

        // Team Routes
        {
          path: '/teams',
          component: MythicPlusTeamPageLayout,
          children: [
            //
            // Team Details
            //
            {
              component: loadable(() => import('../team/containers/MythicPlusTeamDetailsPage')),
              path: namedRoutes.TeamDetails
            },

            // {
            //   component: loadable(() => import('../team/containers/teamDetailsPage')),
            //   path: namedRoutes.EventTeamDetails,
            //
            // },

            {
              component: loadable(() => import('../team/containers/teamRosterPage')),
              path: namedRoutes.TeamRoster
            },
            {
              component: loadable(() => import('../team/containers/teamRecruitmentPage')),
              path: namedRoutes.TeamRecruitment
            },
            {
              component: loadable(() => import('../team/containers/teamStatsPage')),
              path: namedRoutes.TeamStats
            }
          ]
        },

        // Guild Routes
        {
          path: '/guilds',
          component: GuildPageContainer,
          children: [
            {
              component: loadable(() => import('../guild/containers/guildCharacterKeystoneLeaderboardPage')),
              path: namedRoutes.GuildCharacterKeystoneLeaderboard,
              fallback: SkeletonGuildPages
            },
            {
              component: loadable(() => import('../guild/containers/guildRaidCompositionPage')),
              path: namedRoutes.GuildRaidComposition,
              fallback: SkeletonGuildPages
            },
            {
              component: loadable(() => import('../guild/containers/guildCharacterAchievementLeaderboardPage')),
              path: namedRoutes.GuildCharacterAchievementLeaderboard,
              fallback: SkeletonGuildPages
            },
            {
              component: loadable(() => import('../guild/containers/guildRosterPage')),
              path: namedRoutes.GuildRoster,
              fallback: SkeletonGuildPages
            },
            {
              component: loadable(() => import('../guild/containers/guildStatsPage')),
              path: namedRoutes.GuildStats,
              fallback: SkeletonGuildPages
            },
            {
              component: loadable(() => import('../guild/containers/guildRecruitmentPage')),
              path: namedRoutes.GuildRecruitmentPage
            },
            // this should come after the other guild pages since it has a more open route
            // and acts as a fallback
            {
              component: loadable(() => import('../guild/containers/guildDetailsPage')),
              path: namedRoutes.GuildDetails
            }
          ]
        },

        // Event Team Routes
        {
          path: '/event-teams',
          component: loadable(
            () => import('../../../../../shared/frontend/scripts/eventTeams/containers/EventTeamLayout')
          ),
          children: [
            {
              path: namedRoutes.EventTeamDetails,
              component: loadable(
                () => import('../../../../../shared/frontend/scripts/eventTeams/containers/EventTeamDetailsPage')
              )
            }
          ]
        },

        // Event Management Routes (store injected via EventManagementLayout)
        {
          path: '/event-manager',
          component: EventManagementLayout,
          children: [
            {
              path: namedRoutes.EventsManagement,
              component: loadable(() => import('../eventsManagement/containers/EventListPage'))
            },
            {
              path: namedRoutes.EventCollectionList,
              component: loadable(() => import('../eventsManagement/containers/EventCollectionListPage'))
            },
            {
              path: namedRoutes.CreateEventCollection,
              component: loadable(() => import('../eventsManagement/containers/CreateEventCollectionPage'))
            },
            {
              path: namedRoutes.EditEventCollectionDetails,
              component: loadable(() => import('../eventsManagement/containers/EditEventCollectionDetailsPage'))
            },
            {
              path: namedRoutes.EventCollectionEventList,
              component: loadable(() => import('../eventsManagement/containers/EventCollectionEventListPage'))
            },
            {
              path: namedRoutes.EventCollectionVideos,
              component: loadable(() => import('../eventsManagement/containers/EventCollectionVideosListPage'))
            },
            {
              path: namedRoutes.CreateEvent,
              component: loadable(() => import('../eventsManagement/containers/CreateEventPage'))
            },
            {
              path: namedRoutes.EditEvent,
              component: loadable(() => import('../eventsManagement/containers/EditEventPage'))
            },
            {
              path: namedRoutes.CreateLegacyEvent,
              component: loadable(() => import('../eventsManagement/containers/CreateLegacyEventPage'))
            },
            {
              path: namedRoutes.EditLegacyEvent,
              component: loadable(() => import('../eventsManagement/containers/EditLegacyEventPage'))
            },
            {
              path: namedRoutes.CreateEventBracket,
              component: loadable(() => import('../eventsManagement/containers/CreateEventBracketPage'))
            },
            {
              path: namedRoutes.EditEventBracketDetails,
              component: loadable(() => import('../eventsManagement/containers/EditEventBracketDetailsPage'))
            },
            {
              path: namedRoutes.EditEventBracketTeams,
              component: loadable(() => import('../eventsManagement/containers/EditEventBracketTeamsPage'))
            },
            {
              path: namedRoutes.EditEventBracketMatches,
              component: loadable(() => import('../eventsManagement/containers/EditEventBracketMatchesPage'))
            },
            {
              path: namedRoutes.PreviewEventBracket,
              component: loadable(() => import('../eventsManagement/containers/PreviewEventBracketPage'))
            },
            {
              path: namedRoutes.EditEventBracketMatchDetails,
              component: loadable(() => import('../eventsManagement/containers/EditEventBracketMatchDetailsPage'))
            }
          ]
        },

        // Event User Registration Routes

        {
          path: '/events',
          component: loadable(() => import('../eventUserRegistration/containers/EventUserRegistrationLayout')),
          children: [
            {
              component: loadable(() => import('../eventUserRegistration/containers/EventRegisterPage')),
              path: namedRoutes.EventRegister
            },
            {
              component: loadable(() => import('../eventUserRegistration/containers/EventTeamsPage')),
              path: namedRoutes.EventTeams
            },

            {
              component: loadable(
                () => import('../eventUserRegistration/containers/EventBracketsPlunderstormRoyalePage')
              ),
              path: namedRoutes.EventBracketsPlunderstormRoyale
            },
            {
              component: loadable(
                () => import('../eventUserRegistration/containers/EventBracketsTimeTrialsDungeonsPage')
              ),
              path: namedRoutes.EventBracketsTimeTrialsDungeons
            },
            {
              component: loadable(() => import('../eventUserRegistration/containers/EventBracketsTimeTrialsTeamsPage')),
              path: namedRoutes.EventBracketsTimeTrialsTeams
            },
            {
              component: loadable(() => import('../eventUserRegistration/containers/EventBracketsRunsPage')),
              path: namedRoutes.EventBracketsStandardRuns
            },
            {
              component: loadable(() => import('../eventUserRegistration/containers/EventBracketsTeamsPage')),
              path: namedRoutes.EventBracketsStandardTeams
            },
            {
              component: loadable(
                () => import('../eventUserRegistration/containers/EventBracketsTheGreatPushDungeonsPage')
              ),
              path: namedRoutes.EventBracketsTheGreatPush,
              exact: false
            },
            {
              component: loadable(
                () => import('../eventUserRegistration/containers/EventBracketsTheGreatPushLeaderboardPage')
              ),
              path: namedRoutes.EventBracketsTheGreatPushLeaderboard,
              exact: false
            },
            {
              component: loadable(
                () => import('../eventUserRegistration/containers/EventBracketsBreakTheMetaRunsPage')
              ),
              path: namedRoutes.EventBracketsBreakTheMetaRuns
            },
            {
              component: loadable(
                () => import('../eventUserRegistration/containers/EventBracketsBreakTheMetaClassesPage')
              ),
              path: namedRoutes.EventBracketsBreakTheMetaClasses
            },
            {
              component: loadable(() => import('../eventUserRegistration/containers/EventBracketsPage')),
              path: namedRoutes.EventBrackets
            },
            {
              component: loadable(() => import('../eventUserRegistration/containers/EventSectionPage')),
              path: namedRoutes.EventSection
            }
          ]
        },

        // Blizzard Tournament Routes
        {
          path: '/legacy-tournaments',
          component: EventsLayout,
          children: [
            {
              component: loadable(
                () => import('../../../../../shared/frontend/scripts/events/containers/BlizzardEventsPage')
              ),
              path: namedRoutes.BlizzardTournaments
            }
          ]
        },

        // Community Event Routes
        {
          path: '/community-events',
          component: EventsLayout,
          children: [
            {
              component: loadable(
                () => import('../../../../../shared/frontend/scripts/events/containers/CommunityEventsPage')
              ),
              path: namedRoutes.CommunityEvents
            }
          ]
        },

        // Timeline Event Management Routes
        {
          path: '/admin/timeline-manager',
          component: TimelineEventManagementLayout,
          children: [
            {
              component: loadable(() => import('../timelineEventManagement/containers/TimelineEventManagementPage')),
              path: namedRoutes.TimelineEventManagement
            },
            {
              component: loadable(
                () => import('../timelineEventManagement/containers/TimelineEventManagementCreatePage')
              ),
              path: namedRoutes.TimelineEventManagementCreate
            },
            {
              component: loadable(
                () => import('../timelineEventManagement/containers/TimelineEventManagementDetailsPage')
              ),
              path: namedRoutes.TimelineEventManagementDetails
            }
          ]
        },

        // Site Moderation Routes
        {
          path: '/admin/site-moderation',
          component: SiteModerationLayout,
          children: [
            {
              component: loadable(() => import('../siteModeration/containers/SiteModerationBansPage')),
              path: namedRoutes.SiteModerationBans
            },
            {
              component: loadable(() => import('../siteModeration/containers/SiteModerationReportPage')),
              path: namedRoutes.SiteModerationReport
            },
            {
              component: loadable(() => import('../siteModeration/containers/SiteModerationPage')),
              path: namedRoutes.SiteModeration
            }
          ]
        },

        // Normal Routes

        //
        // Home Page
        //
        {
          path: namedRoutes.Home,
          component: HomePage,
          index: true
        },

        {
          component: loadable(() => import('../components/container/clientLiveTrackingExceptionPage')),
          path: namedRoutes.ClientLiveTrackingException
        },

        {
          component: TeamRankingsPage,
          path: namedRoutes.TeamLeaderboardPage
        },
        {
          component: TeamRankingsEventPage,
          path: namedRoutes.TeamLeaderboardEventPage
        },

        {
          component: loadable(() => import('../components/container/keystoneTeamRankingsPage')),
          path: namedRoutes.KeystoneTeamRankings
        },
        {
          component: loadable(() => import('../components/container/keystoneTeamDetailsPage')),
          path: namedRoutes.KeystoneTeamDetails
        },

        {
          component: KeystoneCharacterRankingsPage,
          path: namedRoutes.KeystoneCharacterFactionRankings
        },
        {
          component: KeystoneCharacterRankingsPage,
          path: namedRoutes.KeystoneCharacterFactionRealmRankings
        },
        {
          component: KeystoneCharacterRankingsPage,
          path: namedRoutes.KeystoneCharacterRealmRankings
        },
        {
          component: KeystoneCharacterRankingsPage,
          path: namedRoutes.KeystoneCharacterRankings
        },
        {
          component: loadable(() => import('../components/container/keystoneWarbandRankingsPage')),
          path: namedRoutes.KeystoneWarbandRankings
        },
        {
          component: KeystoneSpecRankingsPage,
          path: namedRoutes.KeystoneSpecFactionRankings
        },
        {
          component: KeystoneSpecRankingsPage,
          path: namedRoutes.KeystoneSpecFactionRealmRankings
        },
        {
          component: KeystoneSpecRankingsPage,
          path: namedRoutes.KeystoneSpecRealmRankings
        },
        {
          component: KeystoneSpecRankingsPage,
          path: namedRoutes.KeystoneSpecRankings
        },
        {
          component: KeystoneRunRankingsPage,
          path: namedRoutes.KeystoneRunFactionAffixRankings
        },
        {
          component: KeystoneRunRankingsPage,
          path: namedRoutes.KeystoneRunFactionRankings
        },
        {
          component: KeystoneRunRankingsPage,
          path: namedRoutes.KeystoneRunAffixRankings
        },
        {
          component: KeystoneRunRankingsPage,
          path: namedRoutes.KeystoneRunRankings
        },
        {
          component: RaidHallOfFamePage,
          path: namedRoutes.RaidHallOfFame
        },
        {
          component: RaidHallOfFamePage,
          path: namedRoutes.RaidFactionHallOfFame
        },
        {
          component: RaidHallOfFamePage,
          path: namedRoutes.RaidRealmHallOfFame
        },
        {
          component: RaidRankingsPage,
          path: namedRoutes.RaidFactionRankings
        },
        {
          component: RaidRankingsPage,
          path: namedRoutes.RaidFactionRealmRankings
        },
        {
          component: RaidRankingsPage,
          path: namedRoutes.RaidRankings,
          fallback: SkeletonRaidRankingsPage
        },
        {
          component: RaidRankingsPage,
          path: namedRoutes.RaidRealmRankings
        },
        {
          component: loadable(() => import('../components/container/raidWorldFirstTrackerPage')),
          path: namedRoutes.RaidWorldFirstTracker
        },
        {
          component: loadable(() => import('../components/container/raidWorldFirstAnalyzePage')),
          path: namedRoutes.RWFAnalyzerPage
        },
        {
          component: loadable(() => import('../globalCoverage/components/coveragePage')),
          path: namedRoutes.RaidGlobalCoverage
        },
        {
          component: BossRankingsPage,
          path: namedRoutes.BossRankings
        },
        {
          component: BossRankingsPage,
          path: namedRoutes.BossFactionRankings
        },
        {
          component: BossRankingsPage,
          path: namedRoutes.BossRealmRankings
        },
        {
          component: BossRankingsPage,
          path: namedRoutes.BossFactionRealmRankings
        },
        {
          component: ProgressRankingsPage,
          path: namedRoutes.ProgressRankings
        },
        {
          component: ProgressRankingsPage,
          path: namedRoutes.ProgressFactionRankings
        },
        {
          component: ProgressRankingsPage,
          path: namedRoutes.ProgressRealmRankings
        },
        {
          component: ProgressRankingsPage,
          path: namedRoutes.ProgressFactionRealmRankings
        },

        {
          component: loadable(() => import('../character/containers/CharacterRecruitmentPage')),
          path: namedRoutes.CharacterRecruitmentPage
        },

        {
          component: loadable(() => import('../character/containers/CharacterStatsPage')),
          path: namedRoutes.CharacterStats
        },

        {
          component: CharacterDetailsPage,
          path: namedRoutes.CharacterDetails
        },

        {
          component: loadable(() => import('../character/containers/snapshotPage')),
          path: namedRoutes.CharacterSnapshot
        },

        {
          component: loadable(() => import('../guild/containers/snapshotPage')),
          path: namedRoutes.GuildSnapshot
        },

        {
          component: loadable(() => import('../components/presentational/ioScorePage')),
          path: namedRoutes.IOScore
        },

        {
          component: loadable(() => import('../components/presentational/raidlePage')),
          path: namedRoutes.RaidlePage
        },

        {
          component: loadable(() => import('../components/presentational/markdownHelpPage')),
          path: namedRoutes.MarkdownHelp
        },

        {
          component: loadable(() => import('../components/presentational/ludicrousSpeedPage')),
          path: namedRoutes.LudicrousSpeed
        },
        {
          component: loadable(() => import('../components/presentational/brandingPage')),
          path: namedRoutes.Branding
        },
        {
          component: loadable(() => import('../components/presentational/addon/addonPage')),
          path: namedRoutes.Addon
        },
        {
          component: loadable(() => import('../components/presentational/premiumCheckoutSuccessPage')),
          path: namedRoutes.PremiumCheckoutSuccess
        },
        {
          component: loadable(() => import('../components/presentational/premiumPage')),
          path: namedRoutes.Premium
        },
        {
          component: loadable(() => import('../components/presentational/recruitmentPage')),
          path: namedRoutes.Recruitment
        },
        {
          component: loadable(() => import('../components/presentational/webhookPage')),
          path: namedRoutes.Discord
        },
        {
          component: loadable(() => import('../components/presentational/widgetPage')),
          path: namedRoutes.Widget
        },
        {
          component: loadable(() => import('../components/presentational/termsOfServicePage')),
          path: namedRoutes.TermsOfService
        },
        {
          component: loadable(() => import('../components/presentational/moderationPolicyPage')),
          path: namedRoutes.ModerationPolicy
        },
        {
          component: loadable(() => import('../components/presentational/privacyPolicyPage')),
          path: namedRoutes.PrivacyPolicy
        },
        {
          component: loadable(() => import('../components/presentational/cookiesPolicyPage')),
          path: namedRoutes.CookiesPolicy
        },
        {
          component: loadable(() => import('../components/presentational/securityDisclosurePage')),
          path: namedRoutes.SecurityDisclosure
        },
        {
          component: loadable(() => import('../components/container/mythicPlusRealmsPage')),
          path: namedRoutes.MythicPlusRealms
        },
        {
          component: loadable(() => import('../components/container/mythicPlusCutoffsPage')),
          path: namedRoutes.MythicPlusCutoffs
        },
        {
          path: namedRoutes.RealmHome,
          component: SpecificHomePage
        },
        {
          path: namedRoutes.RegionHome,
          component: SpecificHomePage
        },
        {
          component: loadable(() => import('../components/container/connectedRealmsPage')),
          path: namedRoutes.ConnectedRealms
        },
        {
          component: loadable(() => import('../components/presentational/mythicKeystoneRunPage')),
          path: namedRoutes.MythicKeystoneRun
        },
        {
          component: loadable(() => import('../components/presentational/mythicKeystoneLoggedRunPage')),
          path: namedRoutes.MythicKeystoneLoggedRun
        },
        {
          component: loadable(() => import('../components/presentational/mythicKeystoneTournamentLiveRun')),
          path: namedRoutes.LiveLoggedRunList
        },
        {
          component: loadable(() => import('../components/presentational/raidLiveEncounter')),
          path: namedRoutes.LiveLoggedEncounterList
        },
        {
          component: loadable(() => import('../components/presentational/raidLoggedEncounterPage')),
          path: namedRoutes.RaidLoggedEncounter
        },
        // mdi 2017
        {
          component: loadable(() => import('../components/container/keystoneTeamRankingsMdi2017Page')),
          path: namedRoutes.KeystoneTeamRankingsMdi2017
        },
        {
          component: loadable(() => import('../components/container/keystoneTeamDetailsMdi2017Page')),
          path: namedRoutes.KeystoneTeamDetailsMdi2017
        },
        {
          component: loadable(() => import('../components/container/mdi/mythicDungeonInvitational2017OverviewPage')),
          path: namedRoutes.MythicDungeonInvitational2017Overview
        },
        {
          component: loadable(() => import('../components/container/mdi/mythicDungeonInvitational2017LandingPage')),
          path: namedRoutes.MythicDungeonInvitational2017Landing
        },

        // mdi 2018
        {
          component: loadable(() => import('../components/container/keystoneTeamRankingsMdi2018Page')),
          path: namedRoutes.KeystoneTeamRankingsMdi2018
        },
        {
          component: loadable(() => import('../components/container/keystoneTimeTrialsTeamRankingsMdi2018Page')),
          path: namedRoutes.KeystoneTimeTrialsTeamRankingsMdi2018
        },
        {
          component: loadable(() => import('../components/container/keystoneTeamDetailsMdi2018Page')),
          path: namedRoutes.KeystoneTeamDetailsMdi2018
        },
        {
          component: loadable(() => import('../components/container/keystoneTimeTrialsTeamDetailsMdi2018Page')),
          path: namedRoutes.KeystoneTimeTrialsTeamDetailsMdi2018
        },
        {
          component: loadable(() => import('../components/container/mdi/mythicDungeonInvitational2018OverviewPage')),
          path: namedRoutes.MythicDungeonInvitational2018Overview
        },
        {
          component: loadable(
            () => import('../components/container/mdi/mythicDungeonInvitational2018RegionalLandingPage')
          ),
          path: namedRoutes.MythicDungeonInvitational2018RegionalLanding
        },
        {
          component: loadable(
            () => import('../components/container/mdi/mythicDungeonInvitational2018GlobalLandingPage')
          ),
          path: namedRoutes.MythicDungeonInvitational2018GlobalLanding
        },
        {
          component: loadable(
            () => import('../components/container/mdi/mythicDungeonInvitational2018ProvingGroundsPage')
          ),
          path: namedRoutes.MythicDungeonInvitational2018ProvingGrounds
        },

        {
          component: loadable(
            () => import('../components/container/mdi/mythicDungeonInvitational2018TimeTrialsPreparationPage')
          ),
          path: namedRoutes.MythicDungeonInvitational2018TimeTrialsPreparation
        },
        {
          component: loadable(
            () => import('../components/container/mdi/mythicDungeonInvitational2018TimeTrialsOverviewPage')
          ),
          path: namedRoutes.MythicDungeonInvitational2018TimeTrialsOverview
        },
        {
          component: loadable(() => import('../components/container/mdi/mythicDungeonInvitational2018StandingsPage')),
          path: namedRoutes.MythicDungeonInvitational2018StandingsPage
        },

        // mdi 2018 all stars
        {
          component: loadable(() => import('../components/container/mdi-2018-allstars/landingPage')),
          path: namedRoutes.MythicDungeonInvitational2018AllStarsLanding
        },
        {
          component: loadable(() => import('../components/container/mdi-2018-allstars/bracketsPage')),
          path: namedRoutes.MythicDungeonInvitational2018AllStarsOverview
        },

        // mdi 2019
        {
          component: loadable(() => import('../components/container/keystoneTeamRankingsMdiSpring2019Page')),
          path: namedRoutes.KeystoneTeamRankingsMdiSpring2019
        },
        {
          component: loadable(() => import('../components/container/keystoneTimeTrialsTeamRankingsMdiSpring2019Page')),
          path: namedRoutes.KeystoneTimeTrialsTeamRankingsMdiSpring2019
        },
        {
          component: loadable(() => import('../components/container/keystoneTeamDetailsMdiSpring2019Page')),
          path: namedRoutes.KeystoneTeamDetailsMdiSpring2019
        },
        {
          component: loadable(() => import('../components/container/keystoneTimeTrialsTeamDetailsMdiSpring2019Page')),
          path: namedRoutes.KeystoneTimeTrialsTeamDetailsMdiSpring2019
        },

        {
          component: loadable(
            () => import('../components/container/mdi-2019-spring/mythicDungeonInvitational2019OverviewPage')
          ),
          path: namedRoutes.MythicDungeonInvitationalSpring2019Overview
        },
        {
          component: loadable(
            () => import('../components/container/mdi-2019-spring/mythicDungeonInvitational2019ProvingGroundsPage')
          ),
          path: namedRoutes.MythicDungeonInvitationalSpring2019ProvingGrounds
        },
        {
          component: loadable(
            () => import('../components/container/mdi-2019-spring/mythicDungeonInvitational2019TimeTrialsOverviewPage')
          ),
          path: namedRoutes.MythicDungeonInvitationalSpring2019TimeTrialsOverview
        },
        {
          component: loadable(
            () =>
              import('../components/container/mdi-2019-spring/mythicDungeonInvitational2019GlobalFinalsOverviewPage')
          ),
          path: namedRoutes.MythicDungeonInvitationalSpring2019FinalsOverview
        },
        {
          component: loadable(
            () => import('../components/container/mdi-2019-spring/mythicDungeonInvitational2019StandingsPage')
          ),
          path: namedRoutes.MythicDungeonInvitationalSpring2019Standings
        },

        {
          component: loadable(() => import('../components/container/keystoneTeamRankingsMdiSummer2019Page')),
          path: namedRoutes.KeystoneTeamRankingsMdiSummer2019
        },
        {
          component: loadable(() => import('../components/container/keystoneTimeTrialsTeamRankingsMdiSummer2019Page')),
          path: namedRoutes.KeystoneTimeTrialsTeamRankingsMdiSummer2019
        },
        {
          component: loadable(() => import('../components/container/keystoneTeamDetailsMdiSummer2019Page')),
          path: namedRoutes.KeystoneTeamDetailsMdiSummer2019
        },
        {
          component: loadable(() => import('../components/container/keystoneTimeTrialsTeamDetailsMdiSummer2019Page')),
          path: namedRoutes.KeystoneTimeTrialsTeamDetailsMdiSummer2019
        },

        {
          component: loadable(
            () => import('../components/container/mdi-2019-summer/mythicDungeonInvitational2019OverviewPage')
          ),
          path: namedRoutes.MythicDungeonInvitationalSummer2019Overview
        },
        {
          component: loadable(
            () => import('../components/container/mdi-2019-summer/mythicDungeonInvitational2019TimeTrialsOverviewPage')
          ),
          path: namedRoutes.MythicDungeonInvitationalSummer2019TimeTrialsOverview
        },
        {
          component: loadable(
            () =>
              import('../components/container/mdi-2019-summer/mythicDungeonInvitational2019GlobalFinalsOverviewPage')
          ),
          path: namedRoutes.MythicDungeonInvitational2019SummerFinalsOverview
        },
        {
          component: loadable(
            () => import('../components/container/mdi-2019-summer/mythicDungeonInvitational2019StandingsPage')
          ),
          path: namedRoutes.MythicDungeonStandingsSummer2019Standings
        },

        // mdi 2020
        {
          component: loadable(() => import('../components/container/mdi-2020-bfa/timeTrialsTeamDetailsPage')),
          path: namedRoutes.MythicDungeonInternationalTimeTrialsTeamDetailsBfa2020
        },
        {
          component: loadable(() => import('../components/container/mdi-2020-bfa/timeTrialsRankingsPage')),
          path: namedRoutes.MythicDungeonInternationalTimeTrialsOverviewBfa2020
        },
        {
          component: loadable(() => import('../components/container/mdi-2020-bfa/timeTrialsTeamRankingsPage')),
          path: namedRoutes.MythicDungeonInternationalTimeTrialsTeamRankingsBfa2020
        },
        {
          component: loadable(() => import('../components/container/mdi-2020-bfa/bracketPage')),
          path: namedRoutes.MythicDungeonInternationalBracketBfa2020
        },
        {
          component: loadable(() => import('../components/container/mdi-2020-bfa/teamStandingsPage')),
          path: namedRoutes.MythicDungeonInternationalStandingsBfa2020
        },

        // mdi sl 2021 season 1
        {
          component: loadable(() => import('../components/container/mdi-2021-sl1/timeTrialsTeamDetailsPage')),
          path: namedRoutes.MythicDungeonInternationalTimeTrialsTeamDetailsSLSeason1
        },
        {
          component: loadable(() => import('../components/container/mdi-2021-sl1/timeTrialsRankingsPage')),
          path: namedRoutes.MythicDungeonInternationalTimeTrialsOverviewSLSeason1
        },
        {
          component: loadable(() => import('../components/container/mdi-2021-sl1/timeTrialsTeamRankingsPage')),
          path: namedRoutes.MythicDungeonInternationalTimeTrialsTeamRankingsSLSeason1
        },
        {
          component: loadable(() => import('../components/container/mdi-2021-sl1/bracketPage')),
          path: namedRoutes.MythicDungeonInternationalBracketSLSeason1
        },
        {
          component: loadable(() => import('../components/container/mdi-2021-sl1/teamStandingsPage')),
          path: namedRoutes.MythicDungeonInternationalStandingsSLSeason1
        },

        // mdi sl 2021 season 2
        {
          component: loadable(() => import('../components/container/mdi-2021-sl2/teamDetailsPage')),
          path: namedRoutes.MythicDungeonInternationalTimeTrialsTeamDetailsSLSeason2
        },
        {
          component: loadable(() => import('../components/container/mdi-2021-sl2/mapRankingsPage')),
          path: namedRoutes.MythicDungeonInternationalTimeTrialsOverviewSLSeason2
        },
        {
          component: loadable(() => import('../components/container/mdi-2021-sl2/teamRankingsPage')),
          path: namedRoutes.MythicDungeonInternationalTimeTrialsTeamRankingsSLSeason2
        },
        {
          component: loadable(() => import('../components/container/mdi-2021-sl2/bracketPage')),
          path: namedRoutes.MythicDungeonInternationalBracketSLSeason2
        },

        // mdi sl 2021 season 3
        {
          component: loadable(() => import('../components/container/mdi-2022-sl3/teamDetailsPage')),
          path: namedRoutes.MythicDungeonInternationalTimeTrialsTeamDetailsSLSeason3
        },
        {
          component: loadable(() => import('../components/container/mdi-2022-sl3/mapRankingsPage')),
          path: namedRoutes.MythicDungeonInternationalTimeTrialsOverviewSLSeason3
        },
        {
          component: loadable(() => import('../components/container/mdi-2022-sl3/teamRankingsPage')),
          path: namedRoutes.MythicDungeonInternationalTimeTrialsTeamRankingsSLSeason3
        },
        {
          component: loadable(() => import('../components/container/mdi-2022-sl3/bracketPage')),
          path: namedRoutes.MythicDungeonInternationalBracketSLSeason3
        },

        // mdi df 2023 season 1
        {
          component: loadable(() => import('../components/container/mdi-2023-df1/teamDetailsPage')),
          path: namedRoutes.MythicDungeonInternationalTimeTrialsTeamDetailsDFSeason1
        },
        {
          component: loadable(() => import('../components/container/mdi-2023-df1/mapRankingsPage')),
          path: namedRoutes.MythicDungeonInternationalTimeTrialsOverviewDFSeason1
        },
        {
          component: loadable(() => import('../components/container/mdi-2023-df1/teamRankingsPage')),
          path: namedRoutes.MythicDungeonInternationalTimeTrialsTeamRankingsDFSeason1
        },
        {
          component: loadable(() => import('../components/container/mdi-2023-df1/bracketPage')),
          path: namedRoutes.MythicDungeonInternationalBracketDFSeason1
        },

        // mdi df great push season 2
        {
          component: loadable(
            () => import('../components/container/mdi-2023-thegreatpush-df-season-2/teamDetailsPage')
          ),
          path: namedRoutes.MythicDungeonInternationalGreatPushDFSeason2TeamDetails
        },
        {
          component: loadable(
            () => import('../components/container/mdi-2023-thegreatpush-df-season-2/qualifiersTeamRankingsPage')
          ),
          path: namedRoutes.MythicDungeonInternationalGreatPushDFSeason2QualifiersTeamRankings
        },
        {
          component: loadable(
            () => import('../components/container/mdi-2023-thegreatpush-df-season-2/qualifiersMapRankingsPage')
          ),
          path: namedRoutes.MythicDungeonInternationalGreatPushDFSeason2QualifiersMapRankings
        },
        {
          component: loadable(
            () => import('../components/container/mdi-2023-thegreatpush-df-season-2/blizzQualifiersTeamRankingsPage')
          ),
          path: namedRoutes.MythicDungeonInternationalGreatPushDFSeason2BlizzQualifiersTeamRankings
        },
        {
          component: loadable(
            () => import('../components/container/mdi-2023-thegreatpush-df-season-2/blizzQualifiersMapRankingsPage')
          ),
          path: namedRoutes.MythicDungeonInternationalGreatPushDFSeason2BlizzQualifiersMapRankings
        },
        {
          component: loadable(
            () => import('../components/container/mdi-2023-thegreatpush-df-season-2/tournamentMapRankingsPage')
          ),
          path: namedRoutes.MythicDungeonInternationalGreatPushDFSeason2TournamentMapRankings
        },
        {
          component: loadable(
            () => import('../components/container/mdi-2023-thegreatpush-df-season-2/tournamentTeamRankingsPage')
          ),
          path: namedRoutes.MythicDungeonInternationalGreatPushDFSeason2TournamentTeamRankings
        },

        // Blizzcon Guild Clash 2023
        {
          component: loadable(() => import('../components/container/blizzcon-guild-clash-2023/bracketPage')),
          path: namedRoutes.BlizzconGuildClash2023
        },

        // mdi sl great push
        {
          component: loadable(() => import('../components/container/mdi-2021-thegreatpush/qualifiersTeamDetailsPage')),
          path: namedRoutes.MythicDungeonInternationalGreatPushQualifiersTeamDetails
        },
        {
          component: loadable(() => import('../components/container/mdi-2021-thegreatpush/qualifiersTeamRankingsPage')),
          path: namedRoutes.MythicDungeonInternationalGreatPushQualifiersTeamRankings
        },
        {
          component: loadable(() => import('../components/container/mdi-2021-thegreatpush/tournamentTeamDetailsPage')),
          path: namedRoutes.MythicDungeonInternationalGreatPushTournamentTeamDetails
        },
        {
          component: loadable(() => import('../components/container/mdi-2021-thegreatpush/tournamentTeamRankingsPage')),
          path: namedRoutes.MythicDungeonInternationalGreatPushTournamentTeamRankings
        },
        {
          component: loadable(() => import('../components/container/mdi-2021-thegreatpush/qualifiersMapRankingsPage')),
          path: namedRoutes.MythicDungeonInternationalGreatPushQualifiersMapRankings
        },
        {
          component: loadable(() => import('../components/container/mdi-2021-thegreatpush/tournamentMapRankingsPage')),
          path: namedRoutes.MythicDungeonInternationalGreatPushTournamentMapRankings
        },

        // mdi sl great push season 2
        {
          component: loadable(
            () => import('../components/container/mdi-2021-thegreatpush-season-2/qualifiersTeamDetailsPage')
          ),
          path: namedRoutes.MythicDungeonInternationalGreatPushSeason2QualifiersTeamDetails
        },
        {
          component: loadable(
            () => import('../components/container/mdi-2021-thegreatpush-season-2/qualifiersTeamRankingsPage')
          ),
          path: namedRoutes.MythicDungeonInternationalGreatPushSeason2QualifiersTeamRankings
        },
        {
          component: loadable(
            () => import('../components/container/mdi-2021-thegreatpush-season-2/tournamentTeamDetailsPage')
          ),
          path: namedRoutes.MythicDungeonInternationalGreatPushSeason2TournamentTeamDetails
        },
        {
          component: loadable(
            () => import('../components/container/mdi-2021-thegreatpush-season-2/tournamentTeamRankingsPage')
          ),
          path: namedRoutes.MythicDungeonInternationalGreatPushSeason2TournamentTeamRankings
        },
        {
          component: loadable(
            () => import('../components/container/mdi-2021-thegreatpush-season-2/qualifiersMapRankingsPage')
          ),
          path: namedRoutes.MythicDungeonInternationalGreatPushSeason2QualifiersMapRankings
        },
        {
          component: loadable(
            () => import('../components/container/mdi-2021-thegreatpush-season-2/tournamentMapRankingsPage')
          ),
          path: namedRoutes.MythicDungeonInternationalGreatPushSeason2TournamentMapRankings
        },

        // mdi sl great push season 4
        {
          component: loadable(() => import('../components/container/mdi-2022-thegreatpush-season-4/teamDetailsPage')),
          path: namedRoutes.MythicDungeonInternationalGreatPushSeason4TeamDetails
        },
        {
          component: loadable(
            () => import('../components/container/mdi-2022-thegreatpush-season-4/qualifiersTeamRankingsPage')
          ),
          path: namedRoutes.MythicDungeonInternationalGreatPushSeason4QualifiersTeamRankings
        },
        {
          component: loadable(
            () => import('../components/container/mdi-2022-thegreatpush-season-4/tournamentTeamRankingsPage')
          ),
          path: namedRoutes.MythicDungeonInternationalGreatPushSeason4TournamentTeamRankings
        },
        {
          component: loadable(
            () => import('../components/container/mdi-2022-thegreatpush-season-4/qualifiersMapRankingsPage')
          ),
          path: namedRoutes.MythicDungeonInternationalGreatPushSeason4QualifiersMapRankings
        },
        {
          component: loadable(
            () => import('../components/container/mdi-2022-thegreatpush-season-4/tournamentMapRankingsPage')
          ),
          path: namedRoutes.MythicDungeonInternationalGreatPushSeason4TournamentMapRankings
        },

        // AFDC 2022
        {
          component: loadable(() => import('../components/container/afdc-2022/tournamentTeamDetailsPage')),
          path: namedRoutes.AFDC2022TournamentTeamDetails
        },
        {
          component: loadable(() => import('../components/container/afdc-2022/tournamentTeamRankingsPage')),
          path: namedRoutes.AFDC2022TournamentTeamRankings
        },
        {
          component: loadable(() => import('../components/container/afdc-2022/tournamentMapRankingsPage')),
          path: namedRoutes.AFDC2022TournamentMapRankings
        },

        {
          component: loadable(() => import('../components/container/afdc-2022-season-4/tournamentTeamDetailsPage')),
          path: namedRoutes.AFDC2022Season4TournamentTeamDetails
        },
        {
          component: loadable(() => import('../components/container/afdc-2022-season-4/tournamentTeamRankingsPage')),
          path: namedRoutes.AFDC2022Season4TournamentTeamRankings
        },
        {
          component: loadable(() => import('../components/container/afdc-2022-season-4/tournamentMapRankingsPage')),
          path: namedRoutes.AFDC2022Season4TournamentMapRankings
        },

        {
          component: loadable(() => import('../globalCoverage/components/tournamentCoveragePage')),
          path: namedRoutes.TournamentGlobalCoverage
        },

        {
          component: loadable(() => import('../globalCoverage/components/pvpTournamentCoveragePage')),
          path: namedRoutes.PvPTournamentGlobalCoverage
        },

        {
          component: loadable(() => import('../globalCoverage/components/transmogCoveragePage')),
          path: namedRoutes.TransmogCoverage
        },
        {
          component: loadable(() => import('../globalCoverage/components/genericGlobalCoveragePage')),
          path: namedRoutes.GenericGlobalCoveragePage
        },

        // tespa
        {
          component: loadable(() => import('../components/container/collegiateDungeonRaceOverviewPage')),
          path: namedRoutes.CollegiateDungeonRaceOverview
        },
        {
          component: loadable(() => import('../components/container/collegiateDungeonRaceLandingPage')),
          path: namedRoutes.CollegiateDungeonRaceLanding
        },

        // dmt 2019
        {
          component: loadable(() => import('../components/container/dmt-2019/bracketsPage')),
          path: namedRoutes.Dmt2019BracketsPage
        },

        // ksm 2020 winterspring
        {
          component: loadable(() => import('../components/container/ksm-2020-winterspring/bracketsPage')),
          path: namedRoutes.Ksm2020BracketsPage
        },
        {
          component: loadable(() => import('../components/container/ksm-2020-winterspring/timeTrialsLeaderboardsPage')),
          path: namedRoutes.Ksm2020TimeTrialsLeaderboardPage
        },
        {
          component: loadable(
            () => import('../components/container/ksm-2020-winterspring/timeTrialsTeamLeaderboardPage')
          ),
          path: namedRoutes.Ksm2020TimeTrialsTeamLeaderboardPage
        },
        {
          component: loadable(() => import('../components/container/ksm-2020-winterspring/timeTrialsTeamDetailsPage')),
          path: namedRoutes.Ksm2020TimeTrialsTeamDetailsPage
        },

        // ksm 2020 fx
        {
          component: loadable(() => import('../components/container/ksm-2020-fx/bracketsPage')),
          path: namedRoutes.Ksm2020FXBracketsPage
        },
        {
          component: loadable(() => import('../components/container/ksm-2020-fx/timeTrialsLeaderboardsPage')),
          path: namedRoutes.Ksm2020FXTimeTrialsLeaderboardPage
        },
        {
          component: loadable(() => import('../components/container/ksm-2020-fx/timeTrialsTeamLeaderboardPage')),
          path: namedRoutes.Ksm2020FXTimeTrialsTeamLeaderboardPage
        },
        {
          component: loadable(() => import('../components/container/ksm-2020-fx/timeTrialsTeamDetailsPage')),
          path: namedRoutes.Ksm2020FXTimeTrialsTeamDetailsPage
        },

        // bloodlust io bracket
        {
          component: loadable(() => import('../components/container/bloodlust-bfa-2020/bracketsPage')),
          path: namedRoutes.BloodlustBFABracket2020
        },
        {
          component: loadable(() => import('../components/container/bloodlust-sl-2021/bracketsPage')),
          path: namedRoutes.BloodlustSLBracket2021
        },

        // keystone builders season 2
        {
          component: loadable(() => import('../components/container/ksb-2021-sl2/teamRankingsPage')),
          path: namedRoutes.KeystoneBuildersSeason2TeamRankingsPage
        },
        {
          component: loadable(() => import('../components/container/ksb-2021-sl2/teamDetailsPage')),
          path: namedRoutes.KeystoneBuildersSeason2TeamDetailsPage
        },

        // keystone builders season 3
        {
          component: loadable(() => import('../components/container/ksb-2023-df1/teamRankingsPage')),
          path: namedRoutes.KeystoneBuildersSeason3TeamRankingsPage
        },
        {
          component: loadable(() => import('../components/container/ksb-2023-df1/teamDetailsPage')),
          path: namedRoutes.KeystoneBuildersSeason3TeamDetailsPage
        },

        {
          component: loadable(() => import('../events/ksb-2023-df1/registrationPage')),
          path: namedRoutes.KeystoneBuildersSeason3Registration
        },
        {
          component: loadable(() => import('../events/ksb-2023-df1/successPage')),
          path: namedRoutes.KeystoneBuildersSeason3RegistrationSuccess
        },

        // Ukraine DF Season 2 Event

        {
          component: loadable(() => import('../events/ukraine-mplus-season-df-2/registrationPage')),
          path: namedRoutes.UkraineTR2023Registration
        },
        {
          component: loadable(() => import('../events/ukraine-mplus-season-df-2/successPage')),
          path: namedRoutes.UkraineTR2023RegistrationSuccess
        },

        {
          component: loadable(() => import('../components/container/ukraine-mplus-df-season-2/bracketPage')),
          path: namedRoutes.UKraineMPlusDFSeason2
        },

        // KSB Season 3 finals
        {
          component: loadable(() => import('../components/container/ksb-2023-df1-finals/tournamentTeamDetailsPage')),
          path: namedRoutes.KeystoneBuildersSeason3FinalsTournamentTeamDetails
        },
        {
          component: loadable(() => import('../components/container/ksb-2023-df1-finals/tournamentTeamRankingsPage')),
          path: namedRoutes.KeystoneBuildersSeason3FinalsTournamentTeamRankings
        },
        {
          component: loadable(() => import('../components/container/ksb-2023-df1-finals/tournamentMapRankingsPage')),
          path: namedRoutes.KeystoneBuildersSeason3FinalsTournamentMapRankings
        },

        // dungeon dojo invitational season 1
        {
          component: loadable(() => import('../components/container/dungeon-dojo-2023-df1/teamRankingsPage')),
          path: namedRoutes.DungeonDojoSeason1TeamRankingsPage
        },

        {
          component: loadable(() => import('../components/container/dungeon-dojo-2023-df1/teamDetailsPage')),
          path: namedRoutes.DungeonDojoSeason1TeamDetailsPage
        },

        // mythic plus charity pro-am 2021
        {
          component: loadable(() => import('../components/container/charity-pro-am-2021/bracketsPage')),
          path: namedRoutes.CharityProAm2021
        },

        // mythic plus charity pro-am 2022
        {
          component: loadable(() => import('../components/container/charity-pro-am-2022/tournamentTeamDetailsPage')),
          path: namedRoutes.CharityProAm2022TournamentTeamDetails
        },
        {
          component: loadable(() => import('../components/container/charity-pro-am-2022/tournamentTeamRankingsPage')),
          path: namedRoutes.CharityProAm2022TournamentTeamRankings
        },
        {
          component: loadable(() => import('../components/container/charity-pro-am-2022/tournamentMapRankingsPage')),
          path: namedRoutes.CharityProAm2022TournamentMapRankings
        },

        // mythic plus charity pro-am 2023
        {
          component: loadable(() => import('../components/container/charity-pro-am-2023/tournamentTeamDetailsPage')),
          path: namedRoutes.CharityProAm2023TournamentTeamDetails
        },
        {
          component: loadable(() => import('../components/container/charity-pro-am-2023/tournamentTeamRankingsPage')),
          path: namedRoutes.CharityProAm2023TournamentTeamRankings
        },
        {
          component: loadable(() => import('../components/container/charity-pro-am-2023/tournamentMapRankingsPage')),
          path: namedRoutes.CharityProAm2023TournamentMapRankings
        },

        // eve ascension 2023
        {
          component: loadable(() => import('../components/container/eve-ascension-2023/tournamentTeamDetailsPage')),
          path: namedRoutes.EveAscension2023TournamentTeamDetails
        },
        {
          component: loadable(() => import('../components/container/eve-ascension-2023/tournamentTeamRankingsPage')),
          path: namedRoutes.EveAscension2023TournamentTeamRankings
        },
        {
          component: loadable(() => import('../components/container/eve-ascension-2023/tournamentMapRankingsPage')),
          path: namedRoutes.EveAscension2023TournamentMapRankings
        },
        // eve ascension df season 2

        {
          component: loadable(() => import('../events/eve-ascension-df-season-2/registrationPage')),
          path: namedRoutes.EveAscensionDFSeason2Registration
        },
        {
          component: loadable(() => import('../events/eve-ascension-df-season-2/successPage')),
          path: namedRoutes.EveAscensionDFSeason2RegistrationSuccess
        },

        // eve ascension df season 2 finals
        {
          component: loadable(
            () => import('../components/container/eve-ascension-df-season-2/tournamentMapRankingsPage')
          ),
          path: namedRoutes.EveAscensionDfS2TournamentMapRankings
        },
        {
          component: loadable(
            () => import('../components/container/eve-ascension-df-season-2/tournamentTeamRankingsPage')
          ),
          path: namedRoutes.EveAscensionDfS2TournamentTeamRankings
        },
        {
          component: loadable(() => import('../components/container/eve-ascension-df-season-2/teamDetailsPage')),
          path: namedRoutes.EveAscensionDfS2TournamentTeamDetails
        },

        // dream gaming
        {
          component: loadable(() => import('../components/container/dream-gaming-2020/bracketsPage')),
          path: namedRoutes.DreamGaming2020BracketsPage
        },
        {
          component: loadable(() => import('../components/container/dream-gaming-2020/timeTrialsLeaderboardsPage')),
          path: namedRoutes.DreamGaming2020TimeTrialsLeaderboardPage
        },
        {
          component: loadable(() => import('../components/container/dream-gaming-2020/timeTrialsTeamLeaderboardPage')),
          path: namedRoutes.DreamGaming2020TimeTrialsTeamLeaderboardPage
        },
        {
          component: loadable(() => import('../components/container/dream-gaming-2020/timeTrialsTeamDetailsPage')),
          path: namedRoutes.DreamGaming2020TimeTrialsTeamDetailsPage
        },

        // search results
        {
          component: loadable(() => import('../GlobalSearch/containers/SearchResultsPage')),
          path: namedRoutes.Search
        },

        {
          component: loadable(() => import('../components/container/news/articlePage')),
          path: namedRoutes.NewsArticle
        },
        {
          component: loadable(() => import('../components/container/news/newsListPage')),
          path: namedRoutes.NewsList
        },
        {
          component: loadable(() => import('../components/container/news/WeeklyRoutesPage')),
          path: namedRoutes.WeeklyRoutes
        },
        {
          component: loadable(() => import('../components/container/news/PlayLikeTheProsPage')),
          path: namedRoutes.PlayLikeThePros
        },

        // BFA In Review Creator
        {
          component: loadable(() => import('../in-review/bfa/containers/creatorPage')),
          path: namedRoutes.MyBFAInReviewCreator
        },

        // SL1 In Review Creator
        {
          component: loadable(() => import('../in-review/sl1/containers/creatorPage')),
          path: namedRoutes.MySL1InReviewCreator
        },

        // SL2 In Review Creator
        {
          component: loadable(() => import('../in-review/sl2/containers/creatorPage')),
          path: namedRoutes.MySL2InReviewCreator
        },

        // SL3 In Review Creator
        {
          component: loadable(() => import('../in-review/sl3/containers/creatorPage')),
          path: namedRoutes.MySL3InReviewCreator
        },

        // SL In Review Creator
        {
          component: loadable(() => import('../in-review/sl/containers/creatorPage')),
          path: namedRoutes.MySLInReviewCreator
        },

        // DF1 In Review Creator
        {
          component: loadable(() => import('../in-review/df1/containers/creatorPage')),
          path: namedRoutes.MyDF1InReviewCreator
        },

        // DF2 In Review Creator
        {
          component: loadable(() => import('../in-review/df2/containers/creatorPage')),
          path: namedRoutes.MyDF2InReviewCreator
        },

        // DF3 In Review Creator
        {
          component: loadable(() => import('../in-review/df3/containers/creatorPage')),
          path: namedRoutes.MyDF3InReviewCreator
        },

        // DF In Review Creator
        {
          component: loadable(() => import('../in-review/df/containers/creatorPage')),
          path: namedRoutes.MyDFInReviewCreator
        },

        // TWW Season 1 In Review Creator
        {
          component: loadable(() => import('../in-review/tww1/containers/creatorPage')),
          path: namedRoutes.MyTWW1InReviewCreator
        },

        // TWW Season 2 In Review Creator
        {
          component: loadable(() => import('../in-review/tww2/containers/creatorPage')),
          path: namedRoutes.MyTWW2InReviewCreator
        },

        // TWW In Review Creator
        {
          component: loadable(() => import('../in-review/tww/containers/creatorPage')),
          path: namedRoutes.MyTWWInReviewCreator
        },

        // user profile
        {
          component: loadable(() => import('../user/containers/userProfilePage')),
          path: namedRoutes.UserProfile
        },
        {
          component: loadable(() => import('../user/containers/userStatsPage')),
          path: namedRoutes.UserStats
        },

        // M+ charity pro-am events
        {
          component: loadable(() => import('../events/charity-pro-am-2022/registrationPage')),
          path: namedRoutes.CharityProAm2022Registration
        },
        {
          component: loadable(() => import('../events/charity-pro-am-2023/registrationPage')),
          path: namedRoutes.CharityProAm2023Registration
        },
        {
          component: loadable(() => import('../events/charity-pro-am-2023/successPage')),
          path: namedRoutes.CharityProAm2023RegistrationSuccess
        },
        {
          component: loadable(() => import('../events/charity-pro-am-2022/successPage')),
          path: namedRoutes.CharityProAm2022RegistrationSuccess
        },

        // Beta key giveaway events
        {
          component: loadable(() => import('../events/giveaways-dragonflight-beta-keys/registrationPage')),
          path: namedRoutes.GiveawayDragonflightBetaKeys2022Entry
        },
        {
          component: loadable(() => import('../events/giveaways-dragonflight-beta-keys/successPage')),
          path: namedRoutes.GiveawayDragonflightBetaKeys2022EntrySuccess
        },

        // Break the Meta 2022
        {
          component: loadable(() => import('../events/break-the-meta-2022/registrationPage')),
          path: namedRoutes.BreakTheMeta2022SignupPage
        },

        // Break the Meta 2023
        {
          component: loadable(() => import('../events/break-the-meta-2023/registrationPage')),
          path: namedRoutes.BreakTheMeta2023SignupPage
        },

        // Break the Meta DF Season 2
        {
          component: loadable(() => import('../events/break-the-meta-df-season-2/registrationPage')),
          path: namedRoutes.BreakTheMetaDfSeason2SignupPage
        },

        // Keystone Event
        {
          path: namedRoutes.KeystoneEventClientUsage,
          component: loadable(() => import('../events/keystoneEventClientUsagePage'))
        },

        // Event Standings
        {
          component: loadable(() => import('../eventStandings/containers/EventStandingsPage')),
          path: namedRoutes.EventStandings
        },
        {
          component: loadable(() => import('../eventStandings/containers/EventInsightsPage')),
          path: namedRoutes.EventInsights
        },

        // Tournaments Hub
        {
          component: loadable(() => import('../tournamentsHub/containers/TournamentsHubPage')),
          path: namedRoutes.TournamentsHub
        },

        {
          component: loadable(() => import('../statistics/containers/StatisticsPage')),
          path: namedRoutes.Statistics
        },
        {
          component: loadable(() => import('../specs/containers/SpecsIndexPage')),
          path: namedRoutes.SpecsIndex
        },
        {
          component: loadable(() => import('../specs/containers/SpecsIndexPage')),
          path: namedRoutes.SpecsIndexWithType
        },
        {
          component: loadable(() => import('../talents/containers/SpecTalentLoadoutComparePage')),
          path: namedRoutes.SpecTalentCompare
        },
        {
          component: loadable(() => import('../specs/containers/SpecStatisticsPage')),
          path: namedRoutes.SpecStatistics
        },

        {
          component: loadable(() => import('../components/container/allRaidsHallOfFamePage')),
          path: namedRoutes.AllRaidsHallOfFame
        },

        {
          component: loadable(() => import('../eventUserRegistration/containers/EmbedBracket')),
          path: namedRoutes.EmbedEventBracket
        },

        // Encounter Group Composition Compare
        {
          component: loadable(() => import('../raiding/containers/GroupCompositionComparePage')),
          path: namedRoutes.GroupCompositionCompare
        },

        // Not Found Page
        {
          component: NotFoundPage,
          path: '/*'
        }
      ]
    },

    //
    // Fullscreen Routes
    //
    {
      component: loadable(() => import('../components/container/policiesUpdatedPage')),
      path: namedRoutes.PoliciesUpdated
    },
    {
      component: loadable(() => import('../components/presentational/forgotPasswordTriggeredPage')),
      path: namedRoutes.ForgotPasswordTriggered
    },
    {
      component: loadable(() => import('../components/presentational/advertisePage')),
      path: namedRoutes.Advertise
    },
    {
      component: loadable(() => import('../components/container/forgotPasswordPage')),
      path: namedRoutes.ForgotPassword
    },
    {
      component: loadable(() => import('../components/container/loginPage')),
      path: namedRoutes.Login
    },
    {
      component: loadable(() => import('../components/container/registerPage')),
      path: namedRoutes.Register
    },
    {
      component: loadable(() => import('../components/presentational/registerPendingPage')),
      path: namedRoutes.RegisterPendingPage
    },
    {
      component: loadable(() => import('../components/presentational/registerErrorPage')),
      path: namedRoutes.RegisterError
    },
    {
      component: loadable(() => import('../components/presentational/registerSuccessPage')),
      path: namedRoutes.RegisterSuccess
    },
    {
      component: loadable(() => import('../components/presentational/registerVerifiedPage')),
      path: namedRoutes.RegisterVerified
    },
    {
      component: loadable(() => import('../components/container/twoFactorAuthPage')),
      path: namedRoutes.TwoFactorAuth
    },
    {
      component: loadable(() => import('../components/presentational/resetPasswordSuccessPage')),
      path: namedRoutes.ResetPasswordSuccess // NOTE: must be before ResetPassword page!
    },
    {
      component: loadable(() => import('../components/container/resetPasswordPage')),
      path: namedRoutes.ResetPassword
    },
    {
      component: loadable(() => import('../components/container/yearInReview2017Page')),
      path: namedRoutes.YearInReview2017
    },
    {
      component: loadable(() => import('../components/container/yearInReview2018Page')),
      path: namedRoutes.YearInReview2018
    },
    {
      component: loadable(() => import('../in-review/bfa/containers/reviewPage')),
      path: namedRoutes.MyBFAInReviewInstance
    },
    {
      component: loadable(() => import('../in-review/sl1/containers/reviewPage')),
      path: namedRoutes.MySL1InReviewInstance
    },
    {
      component: loadable(() => import('../in-review/sl2/containers/reviewPage')),
      path: namedRoutes.MySL2InReviewInstance
    },
    {
      component: loadable(() => import('../in-review/sl3/containers/reviewPage')),
      path: namedRoutes.MySL3InReviewInstance
    },
    {
      component: loadable(() => import('../in-review/sl/containers/reviewPage')),
      path: namedRoutes.MySLInReviewInstance
    },
    {
      component: loadable(() => import('../in-review/df1/containers/reviewPage')),
      path: namedRoutes.MyDF1InReviewInstance
    },
    {
      component: loadable(() => import('../in-review/df2/containers/reviewPage')),
      path: namedRoutes.MyDF2InReviewInstance
    },
    {
      component: loadable(() => import('../in-review/df3/containers/reviewPage')),
      path: namedRoutes.MyDF3InReviewInstance
    },
    {
      component: loadable(() => import('../in-review/df/containers/reviewPage')),
      path: namedRoutes.MyDFInReviewInstance
    },
    {
      component: loadable(() => import('../in-review/tww1/containers/reviewPage')),
      path: namedRoutes.MyTWW1InReviewInstance
    },

    {
      component: loadable(() => import('../in-review/tww2/containers/reviewPage')),
      path: namedRoutes.MyTWW2InReviewInstance
    },
    {
      component: loadable(() => import('../in-review/tww/containers/reviewPage')),
      path: namedRoutes.MyTWWInReviewInstance
    },

    // group charter
    {
      component: loadable(() => import('../components/presentational/groupCharterInvitePage')),
      path: namedRoutes.GroupCharterInvite
    },

    // Stream Embed
    {
      component: loadable(() => import('../streamWidget/containers/bossPullsFeed')),
      path: namedRoutes.StreamEmbedBossPullsFeedWidget
    },
    {
      component: loadable(() => import('../streamWidget/containers/bossProgress')),
      path: namedRoutes.StreamEmbedBossProgressWidget
    },
    {
      component: loadable(() => import('../streamWidget/containers/bossProgressGallery')),
      path: namedRoutes.StreamEmbedBossProgressWidgetGallery
    },
    {
      component: loadable(() => import('../streamWidget/containers/rwfEventFeed')),
      path: namedRoutes.StreamEmbedRwfEventFeedWidget
    },
    {
      component: loadable(() => import('../streamWidget/containers/healthOverAttempt')),
      path: namedRoutes.StreamEmbedHealthOverAttemptWidget
    },
    {
      component: loadable(() => import('../streamWidget/containers/versusWidget')),
      path: namedRoutes.StreamEmbedVersusWidget
    },
    {
      component: loadable(() => import('../streamWidget/containers/versusTimelineWidget')),
      path: namedRoutes.StreamEmbedVersusTimelineWidget
    },
    {
      component: loadable(() => import('../streamWidget/containers/rwfGuildLoot')),
      path: namedRoutes.StreamEmbedRwfGuildLoot
    },
    {
      component: loadable(() => import('../streamWidget/containers/compBreakdown')),
      path: namedRoutes.StreamEmbedCompBreakdown
    },
    {
      component: loadable(() => import('../streamWidget/containers/dungeonsWidget')),
      path: namedRoutes.StreamGreatPushSingleDungeonsWidget
    },
    {
      component: loadable(() => import('../streamWidget/containers/eventsLeaderboardWidget')),
      path: namedRoutes.StreamGreatPushLeaderboardWidget
    },
    {
      component: loadable(() => import('../streamWidget/containers/guildGearComparison')),
      path: namedRoutes.StreamEmbedGuildGearComparison
    },
    {
      component: loadable(() => import('../streamWidget/containers/guildPullsComparison')),
      path: namedRoutes.StreamEmbedGuildPullsComparison
    },
    {
      component: loadable(() => import('../streamWidget/containers/raidLeaderboard')),
      path: namedRoutes.StreamEmbedRaidLeaderboardWidget
    },

    // Guild Boss Progress Monitoring Tool
    {
      component: loadable(() => import('../monitoring/containers/GuildBossProgressMonitoringPage')),
      path: namedRoutes.GuildBossProgressMonitoringTool
    },

    //
    // Ad Testing Pages
    //
    {
      component: loadable(() => import('../components/presentational/videoTestPage')),
      path: namedRoutes.AdTestingVideo
    },

    // File Manager
    {
      component: loadable(
        () => import('../../../../../shared/frontend/scripts/fileExplorer/containers/FileManagerPage')
      ),
      path: namedRoutes.FileManager
    },

    // BattleNet Redirect
    {
      component: loadable(() => import('../components/container/battleNetRedirectPage')),
      path: namedRoutes.BattleNetRedirect
    }
  ];

  const withRootNode = [
    {
      component: RootNode,
      children: routes,
      path: '/'
    }
  ];

  return withRootNode;
}
